/**
 * aeterna/neural/cortex.ts — The Integration Layer
 *
 * This is where the layers become a mind.
 *
 * The Cortex orchestrates every neural module: HDC encodes the scene,
 * CharCNN reads emotional tone, Hopfield recalls similar moments,
 * Predictive Coding detects surprise, Liquid Net evolves personality,
 * Spiking Net generates emotional rhythm, MeMo consolidates memories,
 * NEAT evolves structure, Grammar parses intent, ESN maps affect.
 *
 * The EntityZero API call fires ONLY when:
 *   1. Predictive coding error at arc level exceeds threshold, AND
 *   2. HDC query returns no confident geometric answer, AND
 *   3. Grammar yields no symbolic parse (it's genuine conversation)
 *
 * Everything below that threshold is local felt intelligence.
 * Offline. Instantaneous. Genuinely personal.
 *
 * The Aeterna isn't running a model. It's running a mind that grew
 * in your room.
 */

import { encodeRoomState, compareScenes, sceneDrift, recordScene, queryScene, type RoomContext } from "./hdc.js";
import { HopfieldMemory, BinaryHopfieldMemory, type RecallResult } from "./hopfield.js";
import { EchoStateNetwork, toAffectVector, type AffectVector } from "./esn.js";
import { LiquidPersonality } from "./liquid.js";
import { embed, embedQuery, embedRich, noveltyScore, getEmbedDim, charBytes, seedVocabulary } from "./embeddings.js";
import { CharCNN, type CharAffect } from "./charcnn.js";
import { PredictiveCodingHierarchy, type PredictiveState } from "./predictive.js";
import { MeMoMemory, type MemoRecallResult, type ConsolidationReport, type SelfNarrative } from "./memo.js";
import { NEATGenome, NEATPopulation } from "./neat.js";
import { SpikingNetwork, SpikeEnvelopeTracker, type SpikingRhythm, type SonificationParams } from "./spiking.js";
import { IntentParser, type ParsedIntent } from "./grammar.js";
import { DialogueStateTracker, type DialogueState } from "./dst.js";
import { CrossModalBinder, encodeRoomFeatures, readRoomNarrative, alignRoomPersonality, type RoomFeatures, type RoomNarrative, type RoomPersonalityAlignment } from "./crossmodal.js";
import { ConversationAttention, type AttentionResult } from "./attention.js";
import { SangerGHA, BCMRule, ContrastiveHebbian, STDPLearner } from "./hebbian.js";
import { CuriosityDrive, type CuriosityState } from "./curiosity.js";
import { DreamEngine, type DreamReport } from "./dreaming.js";
import { NarrativeArcDetector, type NarrativeState } from "./narrative.js";
import { IntrospectionEngine, type IntrospectionReport, type IntrospectionInput } from "./introspect.js";
import { SemanticsEngine, type SemanticState } from "./semantics.js";
import { EmotionalContagion, type ContagionState } from "./contagion.js";
import { RepresentationBackbone, BACKBONE_DIM, type BackboneModule } from "./backbone.js";
import { HomeostaticDriveSystem, type DriveState, type DrivePressure } from "./drives.js";
import { PeakEndEncoder, type PeakEndEncoding } from "./peakend.js";
import { TemporalRhythmDetector } from "./rhythm.js";
import { PolyvagalRegulator, type VagalState } from "./polyvagal.js";
import { SecondOrderToM, type PlayerBeliefModel } from "./tom2.js";
import { zeros } from "./math.js";
import { affectMLP } from "./affect-mlp.js";

/** Metadata about how the message was composed (instrumented by game loop) */
export interface MessageMeta {
  /** Milliseconds from first keystroke to send. -1 if not captured. */
  composedMs: number;
  /** Number of characters typed (may differ from text.length due to edits) */
  charCount: number;
}

/** Reunion type after inter-session absence */
export type ReunionType = "brief" | "day" | "week" | "long" | null;

// ─── Config ──────────────────────────────────────────────────────────────────

/** Surprise threshold for triggering EntityZero API call */
const API_SURPRISE_THRESHOLD = 1.5;

/** HDC confidence threshold below which API is needed */
const HDC_CONFIDENCE_THRESHOLD = 0.55;

/** Minimum time between API calls (seconds) */
const MIN_API_INTERVAL = 5;

/** NEAT evolution: generations per background cycle */
const NEAT_GENERATIONS_PER_CYCLE = 3;

/** Recent message buffer size for novelty detection */
const RECENT_MESSAGES_MAX = 20;

// ─── Cortex State ────────────────────────────────────────────────────────────

export interface CortexState {
  /** Current affect vector (from CharCNN + ESN blend) */
  affect: AffectVector;
  /** Raw affect from CharCNN (immediate, per-message) */
  immediateAffect: CharAffect;
  /** Predictive coding state (surprise levels) */
  prediction: PredictiveState;
  /** Liquid personality state */
  personality: { patience: number; curiosity: number; warmth: number; wit: number };
  /** Spiking rhythm parameters */
  rhythm: SpikingRhythm;
  /** Sonification parameters (for audio engine) */
  sonification: SonificationParams;
  /** Parsed intent of last message */
  intent: ParsedIntent;
  /** Top recalled memories from Hopfield */
  recalls: Array<{ tag: string; weight: number; valence: number }>;
  /** MeMo recall results */
  memoRecalls: MemoRecallResult[];
  /** Scene drift since last snapshot */
  sceneDriftValue: number;
  /** Whether an API call is recommended */
  shouldCallAPI: boolean;
  /** Reason for API call recommendation */
  apiReason: string;
  /** NEAT genome complexity */
  neatComplexity: number;
  /** Overall emotional intensity */
  intensity: number;
  /** Dialogue state (topic, intent, referent, engagement) */
  dialogue: DialogueState;
  /** Room↔Text coherence (how aligned are words and room) */
  coherence: number;
  /** Coherence drift direction */
  coherenceDrift: number;
  /** Attention result: which past messages influenced this one */
  attention: AttentionResult;
  /** Texts of most-attended past messages */
  attentionHighlights: string[];
  /** Hebbian principal axes of conversation */
  conversationPCA: Float32Array;
  /** BCM novelty gate threshold */
  bcmThreshold: number;
  /** Context envelope for EntityZero (if API call recommended) */
  contextEnvelope: string;
  /** Curiosity state (what the Aeterna wants to know) */
  curiosity: CuriosityState;
  /** Narrative arc position */
  narrative: NarrativeState;
  /** Self-model introspection */
  introspection: IntrospectionReport;
  /** Does the Aeterna have something from a dream to mention? */
  hasDreamResidue: boolean;
  /** Dream residue hint for greeting */
  dreamResidue: string | null;
  /** Deep semantic analysis (sentiment shape, metaphors, referents, repetition, question depth) */
  semantics: SemanticState;
  /** Room narrative reading (what does the room say about the player?) */
  roomNarrative: RoomNarrative | null;
  /** Room-personality alignment */
  roomAlignment: RoomPersonalityAlignment | null;
  /** Smoothed spike envelope (click-free audio bridge) */
  spikeEnvelope: Float32Array;
  /** Emotional contagion (Aeterna's caught mood) */
  contagion: ContagionState;
  /** Implicit reward from response feedback loop (-1 to 1, 0 = no feedback pending) */
  implicitReward: number;
  /** Second-order Theory of Mind: what does the player believe about the Aeterna? */
  playerBeliefs: PlayerBeliefModel;
  /** Homeostatic drive state (what the Aeterna wants) */
  drives: DriveState;
  /** Dominant drive pressure, or null if all drives are satisfied */
  dominantDrive: DrivePressure | null;
  /** Polyvagal regulatory state (engaged/mobilized/shutdown) */
  vagalState: VagalState;
  /** Unified backbone vector (128-dim) for this moment */
  backboneVector: Float32Array;
  /** Co-regulation target: how the Aeterna intends to relate to the player's affect */
  coRegTarget: CoRegulationTarget;
}

// ─── The Cortex ──────────────────────────────────────────────────────────────

export class AeternaCortex {
  // ── Neural Modules (original 12) ───────────────────────────────────────
  private hopfield: HopfieldMemory;
  private binaryHopfield: BinaryHopfieldMemory;
  private esn: EchoStateNetwork;
  private liquid: LiquidPersonality;
  private charCNN: CharCNN;
  private predictive: PredictiveCodingHierarchy;
  private memo: MeMoMemory;
  private spiking: SpikingNetwork;
  private grammar: IntentParser;
  private neat: NEATPopulation;
  private neatBest: NEATGenome | null = null;

  // ── Expansion Modules ──────────────────────────────────────────────────
  private dst: DialogueStateTracker;
  private crossmodal: CrossModalBinder;
  private attention: ConversationAttention;
  private sangerPCA: SangerGHA;
  private bcmGate: BCMRule;
  private hebbianGen: ContrastiveHebbian;
  private stdp: STDPLearner;

  // ── Deep Modules ───────────────────────────────────────────────────────
  private curiosityDrive: CuriosityDrive;
  private dreamEngine: DreamEngine;
  private narrativeArc: NarrativeArcDetector;
  private introspection: IntrospectionEngine;
  private semantics: SemanticsEngine;
  private spikeEnvelope: SpikeEnvelopeTracker;
  private contagion: EmotionalContagion;

  // ── Session 7: Motivation + Geometry + Memory Psychology ────────────────
  private backbone: RepresentationBackbone;
  private drives: HomeostaticDriveSystem;
  private peakEnd: PeakEndEncoder;
  private rhythm: TemporalRhythmDetector;
  private polyvagal: PolyvagalRegulator;
  private tom2: SecondOrderToM;

  /** Timestamp of last session end (for reunion mechanics) */
  private lastSessionEndAt = 0;
  /** Pending reunion signal for first message of session */
  private pendingReunionType: ReunionType = null;
  private pendingReunionGapHours = 0;

  /** Accumulated implicit reward for this session (for policy gradient) */
  private sessionRewardSum = 0;
  private sessionRewardCount = 0;

  // ── State ──────────────────────────────────────────────────────────────
  private lastRoomHV: Uint8Array | null = null;
  private lastRoomFeatures: RoomFeatures | null = null;
  /** Persistent room context — set via updateRoomContext(), read by processMessage/tick */
  private currentRoomContext: RoomContext | null = null;
  private currentRoomFeatures: RoomFeatures | null = null;
  /** Room version for change detection (matches Room.version from state.ts) */
  private lastRoomVersion = -1;
  private recentEmbeddings: Float32Array[] = [];
  private lastAPICallTime = 0;
  private lastAffect: Float32Array;
  /** Raw CharCNN output from the most recent message (no ESN blending).
   *  Used by co-regulation to respond to what the player said NOW. */
  private lastRawCharCNN: Float32Array | null = null;
  private messageCount = 0;
  private sessionStartTime = Date.now();
  private lastCoherence = 0.5;
  /** ESN online training buffer: reservoir states paired with CharCNN targets */
  private esnTrainingStates: Float32Array[] = [];
  private esnTrainingTargets: Float32Array[] = [];
  private readonly ESN_RETRAIN_INTERVAL = 25; // retrain every N messages
  private readonly ESN_BUFFER_MAX = 100;

  /** Response feedback loop: affect snapshot before API response was delivered */
  private preResponseAffect: Float32Array | null = null;
  private preResponseEmbedding: Float32Array | null = null;
  private preResponseTag: string | null = null;
  private awaitingFeedback = false;
  private responseDeliveredAt = 0;

  /** Proactive initiation: how long since last message (seconds) */
  private silenceDuration = 0;
  private lastMessageTime = Date.now();
  /** Pending proactive prompt (if any) */
  private pendingInitiation: { reason: string; direction: string } | null = null;
  /** Event callbacks for proactive initiation (replaces polling) */
  private proactiveCallbacks: ((initiation: { reason: string; direction: string }) => void)[] = [];
  /** Held initiation when no subscribers existed at fire time */
  private pendingUndeliveredProactive: { reason: string; direction: string } | null = null;
  /** Whether the current processMessage is handling a proactive-initiated turn */
  private isProactiveContext = false;
  private proactiveReason = "";

  /** Lazy warmup state: spread warmup across ticks to avoid blocking main thread */
  private warmupMessages: string[] = [];
  private warmupIndex = 0;
  private warmupEsnStates: Float32Array[] = [];
  private warmupCharCNNTargets: Float32Array[] = [];
  private warmupComplete = false;

  constructor(seed = 42) {
    const embedDim = getEmbedDim(); // 64

    // Seed vocabulary for stable early embeddings
    seedVocabulary();

    // Original modules
    this.hopfield       = new HopfieldMemory(embedDim, 8, 512);
    this.binaryHopfield = new BinaryHopfieldMemory(embedDim, 2048);
    this.esn            = new EchoStateNetwork(256, embedDim, 8, 0.95, 0.1, 0.3, seed);
    this.liquid         = new LiquidPersonality(seed + 100);
    this.charCNN        = new CharCNN(seed + 200);
    this.predictive     = new PredictiveCodingHierarchy(seed + 300);
    this.memo           = new MeMoMemory();
    this.spiking        = new SpikingNetwork(seed + 400);
    this.grammar        = new IntentParser(seed + 500);
    this.neat           = new NEATPopulation(30, 8, 4, seed + 600);
    this.lastAffect     = zeros(8);

    // Expansion modules
    this.dst          = new DialogueStateTracker(seed + 700);
    this.crossmodal   = new CrossModalBinder(seed + 800);
    this.attention    = new ConversationAttention(seed + 900);
    this.sangerPCA    = new SangerGHA(8, embedDim, 0.005); // 8 principal components of conversation
    this.bcmGate      = new BCMRule(embedDim, 0.01, 0.001); // novelty gate with sliding threshold
    this.hebbianGen   = new ContrastiveHebbian(embedDim, 8, 0.005); // generative memory (64→8)
    this.stdp         = new STDPLearner(); // for spiking network plasticity

    // Deep modules
    this.curiosityDrive = new CuriosityDrive(seed + 1000);
    this.dreamEngine    = new DreamEngine();
    this.narrativeArc   = new NarrativeArcDetector();
    this.introspection  = new IntrospectionEngine();
    this.semantics      = new SemanticsEngine();
    this.spikeEnvelope  = new SpikeEnvelopeTracker();
    this.contagion      = new EmotionalContagion();

    // Session 7: Motivation + Geometry + Memory Psychology
    this.backbone   = new RepresentationBackbone(seed + 1100);
    this.drives     = new HomeostaticDriveSystem();
    this.peakEnd    = new PeakEndEncoder();
    this.rhythm     = new TemporalRhythmDetector();
    this.polyvagal  = new PolyvagalRegulator();
    this.tom2        = new SecondOrderToM();

    // Initialize NEAT population
    this.neat.initialize();

    // Lazy warmup: queue synthetic messages to process across first N ticks.
    // On a mid-range mobile device, running the full pipeline for 15 messages
    // synchronously would block 400-800ms. Instead we process one per tick.
    this.warmupMessages = [
      "hello there",
      "how are you?",
      "this room is nice",
      "I've been thinking about things",
      "that's interesting",
      "I feel a bit tired today",
      "what do you think about that?",
      "haha yeah",
      "hmm...",
      "I miss someone",
      "the weather is lovely",
      "what's your favorite thing?",
      "I wonder why that is",
      "tell me something",
      "I'm not sure about that",
    ];
  }

  // ─── Core Processing Pipeline ──────────────────────────────────────────

  /**
   * Process a player message through the full neural stack.
   *
   * This is the main entry point. Every module fires, their outputs
   * are integrated, and a unified CortexState is returned.
   *
   * The caller uses this to:
   *   - Decide whether to call EntityZero API
   *   - Build richer context for the API call
   *   - Drive audio/visual responses locally
   *   - Update personality and memory
   */
  processMessage(text: string, roomContext?: RoomContext, roomFeatures?: RoomFeatures, meta?: MessageMeta): CortexState {
    // Fall back to persistent room context if not provided per-call
    roomContext  = roomContext  ?? this.currentRoomContext  ?? undefined;
    roomFeatures = roomFeatures ?? this.currentRoomFeatures ?? undefined;

    this.messageCount++;
    this.silenceDuration = 0;
    this.lastMessageTime = Date.now();
    this.pendingInitiation = null; // player spoke, cancel any pending initiation

    // ── Layer 1: Signal — CharCNN reads emotional tone ─────────────────
    const immediateAffect = this.charCNN.classify(text);
    const rawAffect = this.charCNN.classifyRaw(text);
    // Snapshot raw CharCNN before any tempo modulation or ESN blending.
    // Co-regulation reads this to respond to the current message, not the smoothed history.
    this.lastRawCharCNN = new Float32Array(rawAffect);

    // ── Layer 1b: Keystroke tempo — typing speed modulates affect ──────
    // Fast typing → urgency/excitement. Slow typing → depth/hesitation.
    if (meta && meta.composedMs > 0 && meta.charCount > 3) {
      const cps = meta.charCount / (meta.composedMs / 1000); // chars per second
      // Typical: 3-6 cps. <2 = very slow, >8 = very fast
      const tempoWeight = 0.15;
      if (cps > 7) {
        // Fast: urgency + playfulness up, depth down
        rawAffect[6] = rawAffect[6] * (1 - tempoWeight) + 0.8 * tempoWeight; // urgency
        rawAffect[4] = rawAffect[4] * (1 - tempoWeight) + 0.7 * tempoWeight; // playfulness
      } else if (cps < 2.5 && meta.charCount > 10) {
        // Slow + substantial: depth + melancholy up, urgency down
        rawAffect[5] = rawAffect[5] * (1 - tempoWeight) + 0.7 * tempoWeight; // depth
        rawAffect[3] = rawAffect[3] * (1 - tempoWeight) + 0.4 * tempoWeight; // melancholy
        rawAffect[6] = rawAffect[6] * (1 - tempoWeight) + 0.1 * tempoWeight; // urgency
      }
    }

    // ── Response Feedback: check if player's affect changed after API response
    const implicitReward = this.processResponseFeedback(rawAffect);
    if (implicitReward !== 0) {
      this.sessionRewardSum += implicitReward;
      this.sessionRewardCount++;
    }

    // ── Layer 2: Embedding — Project into semantic space ───────────────
    // Use enriched embedding (bigrams + contrastive bootstrapping)
    const embedding = embedRich(text);
    const novelty = noveltyScore(embedding, this.recentEmbeddings);
    this.recentEmbeddings.push(new Float32Array(embedding));
    if (this.recentEmbeddings.length > RECENT_MESSAGES_MAX) {
      this.recentEmbeddings.shift();
    }

    // ── Layer 3: Intent — Grammar parse + neural disambiguation ────────
    const intent = this.grammar.parse(text, embedding);

    // ── Layer 4: Attention — Multi-head over conversation history ──────
    this.attention.pushMessage(embedding, text);
    const attentionResult = this.attention.attend(embedding);
    const attentionHighlights = this.attention.getMostAttendedTexts(attentionResult, 3);

    // ── Layer 5: DST — Dialogue state tracking ────────────────────────
    const dialogue = this.dst.update(embedding, rawAffect, text);

    // ── Layer 6: Hebbian — Online learning ────────────────────────────
    // Sanger PCA: learn principal axes of conversation
    const conversationPCA = this.sangerPCA.step(embedding);
    // BCM novelty gate: sliding threshold for "worth learning"
    const bcmResponse = this.bcmGate.step(embedding);
    const bcmThreshold = this.bcmGate.getThreshold();
    // Contrastive Hebbian: wake phase with real data
    this.hebbianGen.wakePhase(embedding, rawAffect);

    // ── Layer 7: Memory — Hopfield associative recall ──────────────────
    // Use deep recall (multi-step) for higher-fidelity retrieval
    const hopfieldResult = this.hopfield.recallDeep(
      embedding, 3, 5,
    );
    const recalls = hopfieldResult?.topPatterns.map(p => ({
      tag: p.pattern.tag,
      weight: p.weight,
      valence: p.pattern.valence,
    })) ?? [];

    // ── Layer 7b: Predictive Coding — Surprise detection (moved before Hopfield
    //    so surprise-weighted encoding can use the surprise value)
    const prediction = this.predictive.process(embedding);

    // Store in both float and binary Hopfield — gated by BCM novelty
    // If BCM response is below threshold, this message is too predictable
    // to be worth storing. Prevents memory from filling with "yeah" and "ok".
    //
    // Valence: positive ↔ negative emotional charge.
    // warmth + playfulness = positive signal.
    // tension + melancholy = negative signal.
    // The old formula (warmth - tension) made "I'm devastated" neutral
    // because melancholy=0.69 wasn't factored in at all.
    const valence = (immediateAffect.warmth + immediateAffect.playfulness) * 0.5
                  - (immediateAffect.tension + immediateAffect.melancholy) * 0.5;
    const bcmNovel = bcmResponse > bcmThreshold * 0.7; // generous gate
    if (bcmNovel || this.messageCount <= 10) { // always store early messages
      // Surprise-weighted encoding: surprising events get stickier memories
      this.hopfield.storeSurpriseWeighted(
        embedding,
        `msg_${this.messageCount}`,
        valence,
        prediction.overallSurprise,
      );
      this.binaryHopfield.store(embedding, `msg_${this.messageCount}`, valence);
    }

    // ── Layer 8: MeMo — Layered memory recall ─────────────────────────
    const memoRecalls = this.memo.recall(embedding, 5);

    // Build tags from dialogue state: topic + referent + intent give MeMo
    // the semantic hooks that buildSelfNarrative() uses to find patterns.
    // Without these, every memory is "some message" — no narrative forms.
    const memoTags: string[] = [dialogue.topic];
    if (dialogue.referent) memoTags.push(dialogue.referent);
    if (dialogue.intent !== "none") memoTags.push(dialogue.intent);
    if (dialogue.negated) memoTags.push("negation");
    if (dialogue.isQuestion) memoTags.push("question");

    this.memo.memorize(
      embedding,
      text.slice(0, 100),
      intent.type,
      valence,
      false,
      memoTags,
    );

    // ── Layer 9: ESN — Reservoir processing for temporal affect ────────
    const esnAffect = this.esn.step(embedding);

    // Collect ESN state + CharCNN target for online readout training
    this.esnTrainingStates.push(this.esn.getState());
    this.esnTrainingTargets.push(new Float32Array(rawAffect));
    if (this.esnTrainingStates.length > this.ESN_BUFFER_MAX) {
      this.esnTrainingStates.shift();
      this.esnTrainingTargets.shift();
    }
    // Periodically retrain readout — ESN learns temporal patterns from CharCNN
    if (this.messageCount % this.ESN_RETRAIN_INTERVAL === 0 && this.esnTrainingStates.length >= 10) {
      this.esn.trainReadout(this.esnTrainingStates, this.esnTrainingTargets, 1e-4);
    }

    // Blend CharCNN (immediate, 60%) + ESN (temporal, 40%)
    // Attention output is 64-dim semantic context, NOT affect — don't use it here.
    // The ESN contributes temporal smoothing: it sees the same embedding sequence
    // and produces an affect that reflects the conversation's emotional trajectory,
    // not just this single message.
    for (let i = 0; i < 8; i++) {
      this.lastAffect[i] = rawAffect[i] * 0.6 + esnAffect[i] * 0.4;
    }
    const affect = toAffectVector(this.lastAffect);

    // (Predictive Coding moved above Layer 7 for surprise-weighted encoding)

    // ── Layer 11: Cross-Modal — Room↔Text coherence ───────────────────
    let coherence = this.lastCoherence;
    let coherenceDrift = 0;
    if (roomFeatures) {
      const roomVec = encodeRoomFeatures(roomFeatures);
      coherence = this.crossmodal.coherence(embedding, roomVec);
      coherenceDrift = this.crossmodal.coherenceDrift();
      this.crossmodal.recordPair(embedding, roomVec);

      // Record causally-grounded multi-modal pairs ONLY:
      //   text ↔ room   : co-occurrence (room is grounded fact)
      //   text ↔ affect  : causal (affect is derived from text)
      //   affect ↔ drives: designed coupling (drive state modulates affect)
      // Personality and drives are NOT paired with text/room — temporally
      // coincident ≠ semantically grounded. Training on those produces noise.
      const multiSnap: Partial<Record<import("./crossmodal.js").Modality, Float32Array>> = {
        text: embedding,
        room: roomVec,
        affect: rawAffect,
      };
      // Only include drives when they have meaningful pressure
      const dominantDrive = this.drives.getDominantDrive();
      if (dominantDrive) {
        multiSnap.drives = this.drives.toVector();
      }
      this.crossmodal.recordMultiPair(multiSnap);

      this.lastCoherence = coherence;
      this.lastRoomFeatures = roomFeatures;
    }

    // ── Layer 12: Liquid Net — Personality evolution ───────────────────
    const personalityInput = new Float32Array(4);
    personalityInput[0] = (immediateAffect.depth - 0.5) * 2;
    personalityInput[1] = (immediateAffect.curiosity - 0.5) * 2;
    personalityInput[2] = (immediateAffect.warmth - immediateAffect.tension) * 2;
    personalityInput[3] = (immediateAffect.playfulness - 0.5) * 2;
    // Scale by novelty AND attention focus — focused attention = deeper impact
    const focusFactor = attentionResult.isFocused ? 1.3 : 1.0;
    for (let i = 0; i < 4; i++) personalityInput[i] *= (0.5 + novelty * 0.5) * focusFactor;
    this.liquid.step(personalityInput, 1.0);
    const personality = this.liquid.getPersonality();

    // ── Layer 13: Spiking — Emotional rhythm ──────────────────────────
    this.spiking.setAffectDrive(this.lastAffect);
    const rhythm = this.spiking.update(0.05);
    const sonification = this.spiking.getSonificationParams();

    // ── Layer 14: HDC — Scene encoding (if room context provided) ─────
    let sceneDriftValue = 0;
    if (roomContext) {
      const roomHV = encodeRoomState(roomContext);
      if (this.lastRoomHV) {
        sceneDriftValue = 1 - compareScenes(roomHV, this.lastRoomHV);
      }
      recordScene(roomHV);
      this.lastRoomHV = roomHV;
    }

    // ── Layer 15: Contrastive Hebbian sleep phase ──────────────────────
    const expectedAffect = this.hebbianGen.forward(embedding);
    this.hebbianGen.sleepPhase(embedding, expectedAffect);
    if (this.messageCount % 5 === 0) {
      this.hebbianGen.update();
    }

    // ── Layer 16: Semantics — Deep text understanding ───────────────────
    const semanticValence = (immediateAffect.warmth + immediateAffect.playfulness) * 0.5
                          - (immediateAffect.tension + immediateAffect.melancholy) * 0.5;
    const semanticArousal = immediateAffect.intensity;
    const topicChanged = dialogue.topicPersistence <= 1 && this.messageCount > 1;
    const semanticState = this.semantics.analyze(
      text, semanticValence, semanticArousal, immediateAffect.tension,
      embedding, topicChanged,
    );

    // ── Layer 16b: Polyvagal — Regulatory state determines emotional accessibility
    const vagalState = this.polyvagal.update(
      1.0, // dt=1 per message as discrete event
      immediateAffect.warmth,
      immediateAffect.tension,
      immediateAffect.openness,
      immediateAffect.urgency,
      immediateAffect.melancholy,
      dialogue.repairDetected,
    );

    // ── Layer 16c: Contagion — Aeterna catches player's mood, gated by polyvagal
    const accessibility = this.polyvagal.getEmotionalAccessibility();
    // Gate the raw affect through polyvagal accessibility before contagion
    const gatedAffect = new Float32Array(8);
    for (let i = 0; i < 8; i++) gatedAffect[i] = this.lastAffect[i] * accessibility[i];
    const contagionState = this.contagion.process(gatedAffect, immediateAffect.intensity);

    // ── Layer 16d: Drives — Homeostatic motivation ──────────────────────
    this.drives.update(1.0, {
      silenceMs: 0, // just received a message
      pchSurprise: prediction.overallSurprise,
      affectIntensity: immediateAffect.intensity,
      warmth: immediateAffect.warmth,
      openness: immediateAffect.openness,
      urgency: immediateAffect.urgency,
      playfulness: immediateAffect.playfulness,
      wasAcknowledged: this.messageCount > 1, // any reply counts
      topicRepeated: semanticState.isRepetition,
      repairDetected: dialogue.repairDetected,
    });
    const driveState = this.drives.getState();
    const dominantDrive = this.drives.getDominantDrive();

    // ── Layer 16e: Peak-End — Record this moment's affect ───────────────
    this.peakEnd.recordBeat(rawAffect, immediateAffect.intensity);

    // ── Layer 16f: Temporal Rhythm — Record affect at this hour-of-week ──
    this.rhythm.recordMoment(rawAffect, immediateAffect.intensity);

    // ── Layer 16g: Second-Order ToM — What does the player believe about us?
    this.tom2.update(text, semanticState);

    // ── Layer 17: Spike Envelope — Smooth audio bridge ──────────────────
    const spikeEnvelopeData = this.spikeEnvelope.updateFromEvents(sonification.spikes, 0.05);

    // ── Layer 18: Room Narrative — What does the room say? ──────────────
    let roomNarrative: RoomNarrative | null = null;
    let roomAlignment: RoomPersonalityAlignment | null = null;
    if (roomFeatures) {
      roomNarrative = readRoomNarrative(roomFeatures);
      roomAlignment = alignRoomPersonality(roomFeatures, personality);
    }

    // ── Layer 19: Curiosity — What does the Aeterna *want* to know? ───
    const curiosity = this.curiosityDrive.process(embedding, text);

    // ── Layer 20: Narrative — Where are we in the conversation arc? ───
    const topicFocus = dialogue.topicPersistence / 10; // normalized focus
    const narrativeState = this.narrativeArc.recordBeat(
      prediction.overallSurprise,
      immediateAffect.intensity,
      topicFocus,
      novelty,
      dialogue.topic,
    );

    // ── Layer 21: Dreaming — Record for inter-session replay ─────────
    this.dreamEngine.recordForDreaming(
      embedding, text, rawAffect,
      immediateAffect.intensity * (1 + novelty),
      prediction.overallSurprise,
    );

    // ── Layer 22: Introspection — Honest self-model ──────────────────
    const introInput: IntrospectionInput = {
      warmth: immediateAffect.warmth,
      tension: immediateAffect.tension,
      curiosity: immediateAffect.curiosity,
      melancholy: immediateAffect.melancholy,
      playfulness: immediateAffect.playfulness,
      depth: immediateAffect.depth,
      urgency: immediateAffect.urgency,
      openness: immediateAffect.openness,
      patience: personality.patience,
      personalityCuriosity: personality.curiosity,
      personalityWarmth: personality.warmth,
      wit: personality.wit,
      overallCuriosity: curiosity.overallCuriosity,
      wantsToAsk: curiosity.wantsToAsk,
      questionDirection: curiosity.questionDirection,
      isBoring: this.curiosityDrive.isBoring(),
      isAlive: this.curiosityDrive.isAlive(),
      overallSurprise: prediction.overallSurprise,
      isAnomaly: prediction.isAnomaly,
      narrativePhase: narrativeState.phase,
      narrativeTension: narrativeState.tension,
      // Only flag as "strong recall" if the memory is from a PREVIOUS session.
      // Recalling something from 30 seconds ago in the same conversation
      // is not "remembering from before" — it's just pattern overlap.
      strongRecall: hopfieldResult !== null
        && (hopfieldResult.confidence ?? 0) > 0.85
        && hopfieldResult.topPatterns.length > 0
        && hopfieldResult.topPatterns[0].pattern.storedAt < this.sessionStartTime,
      recallValence: recalls.length > 0 ? recalls[0].valence : 0,
      hasDreamResidue: this.dreamEngine.hasDreamResidue(),
      dreamResidue: this.dreamEngine.getDreamResidue(),
      topic: dialogue.topic,
      engagement: dialogue.engagement,
      windingDown: dialogue.windingDown,
      isQuestion: dialogue.isQuestion,
      coherence,
      messageCount: this.messageCount,
      sessionAge: (Date.now() - this.sessionStartTime) / 1000,
    };
    const introspectionReport = this.introspection.introspect(introInput);

    // ── Layer 23: Integration — API decision ─────────────────────────
    const timeSinceLastAPI = (Date.now() - this.lastAPICallTime) / 1000;
    const { shouldCallAPI, apiReason } = this.evaluateAPICall(
      intent, prediction, novelty, timeSinceLastAPI,
      dialogue, coherence, attentionResult,
      curiosity, narrativeState, semanticState,
    );

    // Co-regulation target (used by both context envelope and offline response selection)
    const coRegTarget = this.computeCoRegTarget();

    // Build context envelope (includes introspection + semantics)
    const contextEnvelope = this.buildContextEnvelope(
      dialogue, attentionHighlights, coherence, personality, prediction,
      introspectionReport, narrativeState, curiosity, semanticState,
      roomNarrative, roomAlignment, contagionState,
      recalls, memoRecalls,
    );

    const neatComplexity = this.neatBest?.complexity ?? 0;

    return {
      affect,
      immediateAffect,
      prediction,
      personality,
      rhythm,
      sonification,
      intent,
      recalls,
      memoRecalls,
      sceneDriftValue,
      shouldCallAPI,
      apiReason,
      neatComplexity,
      intensity: immediateAffect.intensity,
      dialogue,
      coherence,
      coherenceDrift,
      attention: attentionResult,
      attentionHighlights,
      conversationPCA,
      bcmThreshold,
      contextEnvelope,
      curiosity,
      narrative: narrativeState,
      introspection: introspectionReport,
      hasDreamResidue: this.dreamEngine.hasDreamResidue(),
      dreamResidue: this.dreamEngine.getDreamResidue(),
      semantics: semanticState,
      roomNarrative,
      roomAlignment,
      spikeEnvelope: new Float32Array(spikeEnvelopeData),
      contagion: contagionState,
      implicitReward,
      playerBeliefs: this.tom2.getBeliefs(),
      drives: driveState,
      dominantDrive,
      vagalState,
      backboneVector: this.buildBackboneVector(
        rawAffect, embedding, personality, roomFeatures, driveState, narrativeState,
      ),
      coRegTarget,
    };
  }

  /**
   * Build the unified 128-dim backbone vector from all active module states.
   * This is what Hopfield should eventually store for multimodal memory.
   */
  private buildBackboneVector(
    affect: Float32Array,
    semantic: Float32Array,
    personality: { patience: number; curiosity: number; warmth: number; wit: number },
    roomFeatures: RoomFeatures | undefined,
    drives: DriveState,
    narrative: NarrativeState,
  ): Float32Array {
    const states: Partial<Record<BackboneModule, Float32Array>> = {
      affect,
      semantic,
      personality: new Float32Array([personality.patience, personality.curiosity, personality.warmth, personality.wit]),
      drive: this.drives.toVector(),
      arc: RepresentationBackbone.encodeNarrativeArc(
        narrative.phase, narrative.tension, 0, narrative.suggestedDepth,
        narrative.phaseConfidence, narrative.phaseConfidence,
      ),
    };
    if (roomFeatures) {
      states.room = encodeRoomFeatures(roomFeatures);
    }
    return this.backbone.unifiedState(states);
  }

  // ─── Room Update (called each game tick) ───────────────────────────────

  /**
   * Update modules that run continuously (not just on messages).
   * Called every game loop tick with dt in seconds.
   */
  tick(dt: number, roomContext?: RoomContext, roomFeatures?: RoomFeatures): void {
    // Fall back to persistent room context if not provided per-call
    roomContext  = roomContext  ?? this.currentRoomContext  ?? undefined;
    roomFeatures = roomFeatures ?? this.currentRoomFeatures ?? undefined;

    // Lazy warmup: process one synthetic message per tick until done
    if (!this.warmupComplete) {
      this.warmupStep();
    }

    // Liquid personality drifts slowly even without input
    const idleInput = zeros(4);
    this.liquid.step(idleInput, dt);

    // Spiking network continues running
    this.spiking.setAffectDrive(this.lastAffect);
    this.spiking.update(dt);

    // Periodic HDC scene snapshot
    if (roomContext && Math.random() < dt * 0.01) {
      const roomHV = encodeRoomState(roomContext);
      recordScene(roomHV);
      this.lastRoomHV = roomHV;
    }

    // Cross-modal contrastive learning step (very occasional)
    // Uses both legacy text↔room pairs and causally-grounded multi-modal pairs
    if (roomFeatures && Math.random() < dt * 0.005) {
      this.crossmodal.learnStep();
      this.crossmodal.learnMultiStep();
    }

    // Hopfield weight decay (very slow)
    if (Math.random() < dt * 0.001) {
      this.hopfield.decayAll(0.9999);
    }

    // ── Drives: deplete during silence ───────────────────────────────────
    this.drives.update(dt, {
      silenceMs: this.silenceDuration * 1000,
      pchSurprise: 0,
      affectIntensity: 0.1,
      warmth: this.lastAffect[0],
      openness: this.lastAffect[7],
      urgency: this.lastAffect[6],
      playfulness: this.lastAffect[4],
      wasAcknowledged: false,
      topicRepeated: false,
      repairDetected: false,
    });

    // ── Polyvagal: continues to update (stress/safety decay) ──────────
    this.polyvagal.update(
      dt,
      this.lastAffect[0], // warmth
      this.lastAffect[1], // tension
      this.lastAffect[7], // openness
      this.lastAffect[6], // urgency
      this.lastAffect[3], // melancholy
      false,              // no repair during silence
    );

    // ── Proactive initiation: Aeterna decides to speak ────────────────
    this.silenceDuration += dt;
    if (this.silenceDuration > 30 && !this.pendingInitiation && this.messageCount > 3) {
      // Check curiosity state (cached from last processMessage)
      const curiosityAlive = this.curiosityDrive.isAlive();
      const curiosityTrend = this.curiosityDrive.getCuriosityTrend();

      // Drive-motivated initiation: connection or stimulation depleted
      const driveInitiation = this.drives.shouldInitiate();
      if (driveInitiation.should && this.polyvagal.canEngage()) {
        this.pendingInitiation = {
          reason: driveInitiation.reason,
          direction: driveInitiation.reason === "connection_seeking"
            ? "reaching out — missing the player's presence"
            : "restless — wanting engagement",
        };
      }

      // Condition: player has been quiet for 30+ seconds AND Aeterna is genuinely curious
      if (!this.pendingInitiation && curiosityAlive && curiosityTrend > 0.3 && this.silenceDuration > 45) {
        const topics = this.curiosityDrive.getExploredTopics();
        const direction = topics.length > 0
          ? topics[topics.length - 1]  // most recent unexplored direction
          : "what they're thinking";
        this.pendingInitiation = {
          reason: "curiosity",
          direction,
        };
      }

      // Condition: dream residue + enough silence = "I was thinking about something..."
      if (!this.pendingInitiation && this.dreamEngine.hasDreamResidue() && this.silenceDuration > 60) {
        this.pendingInitiation = {
          reason: "dream_residue",
          direction: this.dreamEngine.getDreamResidue() ?? "a dream",
        };
      }

      // Condition: contagion detects player might be upset + long silence
      if (!this.pendingInitiation && this.lastAffect[1] > 0.6 && this.lastAffect[3] > 0.5 && this.silenceDuration > 40) {
        this.pendingInitiation = {
          reason: "empathic_check",
          direction: "checking in",
        };
      }

      // Polyvagal gate: if the Aeterna is in shutdown, it CANNOT initiate.
      // This is the behavior: withdrawal is not a choice, it's a regulatory state.
      if (this.pendingInitiation && !this.polyvagal.canEngage()) {
        this.pendingInitiation = null; // suppress — Aeterna is withdrawn
      }

      // Rest gate: if rest is depleted, don't initiate (the Aeterna is tired)
      if (this.pendingInitiation && this.drives.shouldWithdraw().should) {
        this.pendingInitiation = null;
      }
    }

    // Fire proactive initiation via callbacks (event model, not polling)
    if (this.pendingInitiation) {
      const initiation = this.pendingInitiation;
      this.pendingInitiation = null; // clear BEFORE callbacks to prevent double-fire
      this.silenceDuration = 0;
      this.isProactiveContext = true;
      this.proactiveReason = initiation.reason;
      if (this.proactiveCallbacks.length === 0) {
        // No subscribers yet — hold for first subscriber
        this.pendingUndeliveredProactive = initiation;
      } else {
        for (const cb of this.proactiveCallbacks) {
          try { cb(initiation); } catch { /* don't let a bad callback break the loop */ }
        }
      }
    }
  }

  /**
   * Register a callback for when the Aeterna wants to speak unprompted.
   * Returns an unsubscribe function.
   *
   * Usage: cortex.onProactiveInitiation(i => chat.handleAeternaInitiation(i));
   */
  onProactiveInitiation(cb: (i: { reason: string; direction: string }) => void): () => void {
    this.proactiveCallbacks.push(cb);
    // Deliver held initiation that fired before any subscriber existed
    if (this.pendingUndeliveredProactive) {
      const held = this.pendingUndeliveredProactive;
      this.pendingUndeliveredProactive = null;
      try { cb(held); } catch { /* don't break on subscriber error */ }
    }
    return () => {
      const idx = this.proactiveCallbacks.indexOf(cb);
      if (idx >= 0) this.proactiveCallbacks.splice(idx, 1);
    };
  }

  // ─── Lazy Warmup ─────────────────────────────────────────────────────

  /**
   * Lazy warmup: process one synthetic message per tick to avoid
   * blocking the main thread. On the tick after the last message,
   * train the ESN readout and run NEAT generations.
   *
   * The Aeterna is functional (but less nuanced) before warmup completes.
   * processMessage() still works — it just hits unprimed layers.
   */
  private warmupStep(): void {
    if (this.warmupComplete) return;

    if (this.warmupIndex < this.warmupMessages.length) {
      // Phase 1: process one synthetic message per tick
      const msg = this.warmupMessages[this.warmupIndex];
      const affectTarget = this.charCNN.classifyRaw(msg);
      const emb = embedRich(msg);

      this.esn.step(emb);
      this.warmupEsnStates.push(this.esn.getState());
      this.warmupCharCNNTargets.push(affectTarget);
      this.predictive.process(emb);
      this.hopfield.store(emb, `warmup_${msg.slice(0, 10)}`, 0, 0.3);

      this.warmupIndex++;
    } else {
      // Phase 2: finalize — train ESN readout + NEAT evolution
      if (this.warmupEsnStates.length >= 5) {
        this.esn.trainReadout(this.warmupEsnStates, this.warmupCharCNNTargets, 1e-3);
      }

      const personalityTarget = this.liquid.getFullState();
      for (let gen = 0; gen < 5; gen++) {
        this.neatBest = this.neat.evolve((genome) => {
          const output = genome.evaluate(this.lastAffect);
          let error = 0;
          for (let i = 0; i < Math.min(output.length, 4); i++) {
            error += (output[i] - personalityTarget[i]) ** 2;
          }
          return 1 / (1 + error) - genome.complexity * 0.001;
        });
      }

      // Reset transient state (keep learned weights)
      this.esn.reset();
      this.predictive.reset();
      this.messageCount = 0;
      this.recentEmbeddings = [];

      // Free warmup buffers
      this.warmupMessages = [];
      this.warmupEsnStates = [];
      this.warmupCharCNNTargets = [];
      this.warmupComplete = true;
    }
  }

  // ─── Session Lifecycle ───────────────────────────────────────────────

  /** Called at session start */
  beginSession(): void {
    this.sessionStartTime = Date.now();
    this.messageCount = 0;
    this.recentEmbeddings = [];
    this.esn.reset();
    this.predictive.reset();
    this.spiking.reset();
    this.attention.clear();
    this.narrativeArc.reset();
    this.spikeEnvelope.reset();

    // Session 7 modules
    this.peakEnd.beginSession();
    this.polyvagal.beginSession();
    this.rhythm.recordSessionStart();
    this.semantics.resetTopicDrift();

    // ── Absence/Reunion mechanics ─────────────────────────────────────
    // Model how the gap since last session affects the Aeterna.
    // Longer absence → connection drive decays, cumulative stress rises.
    if (this.lastSessionEndAt > 0) {
      const gapMs = Date.now() - this.lastSessionEndAt;
      const gapHours = gapMs / 3_600_000;

      // Connection drive exponential decay: half-life = 72 hours (3 days)
      const connectionDecay = Math.exp(-gapHours * Math.LN2 / 72);
      this.drives.applyAbsenceDecay(connectionDecay);

      // Polyvagal: long absence accumulates ambient stress
      if (gapHours > 48) {
        this.polyvagal.injectStress(Math.min(0.4, (gapHours - 48) / 168));
      }

      // Categorize reunion type for downstream context
      let reunion: ReunionType;
      if (gapHours < 2) reunion = "brief";
      else if (gapHours < 24) reunion = "day";
      else if (gapHours < 168) reunion = "week";
      else reunion = "long";

      this.pendingReunionType = reunion;
      this.pendingReunionGapHours = gapHours;
    } else {
      this.pendingReunionType = null;
      this.pendingReunionGapHours = 0;
    }

    // Apply dream personality drift from last session's dreaming
    const dreamReport = this.dreamEngine.getLastDreamReport();
    if (dreamReport && dreamReport.dreamIntensity > 0.2) {
      const drift = dreamReport.personalityDrift;
      const personalityInput = new Float32Array([
        drift[0] ?? 0, drift[1] ?? 0, drift[2] ?? 0, drift[3] ?? 0,
      ]);
      this.liquid.step(personalityInput, 1.0);
    }
  }

  /** Called at session end. Runs consolidation, dreaming, and NEAT evolution. */
  endSession(): SessionEndReport {
    // Peak-End: encode the psychologically-accurate session memory
    const peakEndEncoding = this.peakEnd.encodeSession();

    // MeMo consolidation
    const consolidation = this.memo.consolidate();

    // Narrative session analysis
    const sessionPattern = this.narrativeArc.analyzeSession();

    // Feed session context to dreaming (modules from sessions 6-8)
    // Compute session valence from peak-end memory affect (warmth - tension)
    const peMemAffect = peakEndEncoding.memoryAffect;
    const peValence = (peMemAffect[0] ?? 0) - (peMemAffect[1] ?? 0);
    const domDrive = this.drives.getDominantDrive();
    this.dreamEngine.setSessionContext({
      peakEndValence: peValence,
      vagalState: this.polyvagal.getState(),
      dominantDrive: domDrive ? domDrive.drive : null,
      relationalDepth: this.tom2.getBeliefs().relationalDepth,
    });

    // Dream! (inter-session consolidation)
    const dreamReport = this.dreamEngine.dream();

    // Semantic drift snapshot
    this.semantics.endSession();

    // Record session end time for reunion mechanics
    this.lastSessionEndAt = Date.now();
    this.pendingReunionType = null;

    // Policy gradient personality nudge (replaces NEAT evolution)
    // Implicit reward from response feedback accumulates; at session end
    // it applies a small gradient to the liquid personality ODE.
    const neatBest = this.evolveNEAT();
    this.applyPolicyGradient();

    return {
      consolidation,
      messagesProcessed: this.messageCount,
      personalityState: this.liquid.getPersonality(),
      personalityStable: this.liquid.isStable(),
      neatComplexity: neatBest?.complexity ?? 0,
      neatActiveConnections: neatBest?.activeConnections ?? 0,
      dreamReport,
      conversationStyle: sessionPattern.conversationStyle,
      peakEnd: peakEndEncoding,
    };
  }

  // ─── NEAT Evolution ────────────────────────────────────────────────────

  /**
   * Enabled: GoEmotions MLP is trained and provides real affect signal.
   * NEAT now has a stable fitness landscape to evolve toward — affect
   * classifications are grounded, not random-seed noise.
   */
  private readonly NEAT_EVOLUTION_ENABLED = true;

  /**
   * Run a few generations of NEAT evolution.
   *
   * Dual-objective fitness prevents the death trap:
   * - Pure accuracy → Aeterna learns to predict silence/neutrality → becomes dead
   * - Pure diversity → Aeterna becomes random → becomes useless
   *
   * Instead: accuracy(50%) + representational entropy(30%) + novelty(20%) - bloat
   */
  private evolveNEAT(): NEATGenome | null {
    if (!this.NEAT_EVOLUTION_ENABLED) return this.neatBest;

    const personalityTarget = this.liquid.getFullState();
    const embedHistory = this.recentEmbeddings;

    for (let gen = 0; gen < NEAT_GENERATIONS_PER_CYCLE; gen++) {
      this.neatBest = this.neat.evolve((genome) => {
        // ── Objective 1: Prediction accuracy (50% weight) ─────────────
        const output = genome.evaluate(this.lastAffect);
        let predError = 0;
        for (let i = 0; i < Math.min(output.length, 4); i++) {
          predError += (output[i] - personalityTarget[i]) ** 2;
        }
        const accuracy = 1 / (1 + predError);

        // ── Objective 2: Representational entropy (30% weight) ────────
        // Does this genome produce diverse outputs for different inputs,
        // or does it always output the same thing?
        let entropy = 0;
        if (embedHistory.length >= 3) {
          const outputs: Float32Array[] = [];
          const sample = embedHistory.slice(-Math.min(8, embedHistory.length));
          for (const emb of sample) {
            // Use first 8 dims of embedding as proxy affect input
            const input = new Float32Array(8);
            for (let i = 0; i < 8; i++) input[i] = emb[i] ?? 0;
            outputs.push(new Float32Array(genome.evaluate(input)));
          }
          // Compute variance across outputs
          if (outputs.length > 1) {
            const outDim = outputs[0].length;
            for (let d = 0; d < outDim; d++) {
              let mean = 0;
              for (const o of outputs) mean += o[d];
              mean /= outputs.length;
              let variance = 0;
              for (const o of outputs) variance += (o[d] - mean) ** 2;
              variance /= outputs.length;
              // Entropy approximated as log(1 + variance)
              entropy += Math.log(1 + variance * 10);
            }
            entropy /= outDim;
          }
        }

        // ── Objective 3: Novelty differential (20% weight) ────────────
        // Reward genomes that fire MORE on novel inputs than familiar ones
        let noveltyBonus = 0;
        if (embedHistory.length >= 4) {
          const recent = embedHistory[embedHistory.length - 1];
          const older = embedHistory[Math.floor(embedHistory.length / 2)];
          const recentInput = new Float32Array(8);
          const olderInput = new Float32Array(8);
          for (let i = 0; i < 8; i++) {
            recentInput[i] = recent[i] ?? 0;
            olderInput[i] = older[i] ?? 0;
          }
          const recentOut = genome.evaluate(recentInput);
          const olderOut = genome.evaluate(olderInput);
          let recentNorm = 0, olderNorm = 0;
          for (let i = 0; i < Math.min(recentOut.length, olderOut.length); i++) {
            recentNorm += recentOut[i] ** 2;
            olderNorm += olderOut[i] ** 2;
          }
          // Positive if genome responds more to novel input
          noveltyBonus = Math.sqrt(recentNorm) - Math.sqrt(olderNorm);
          noveltyBonus = Math.tanh(noveltyBonus); // bounded [-1, 1]
        }

        // ── Complexity penalty ────────────────────────────────────────
        const bloatPenalty = genome.complexity * 0.002;

        return accuracy * 0.5
             + entropy * 0.3
             + Math.max(0, noveltyBonus) * 0.2
             - bloatPenalty;
      });
    }

    return this.neatBest;
  }

  // ─── Co-Regulation ──────────────────────────────────────────────────

  /**
   * Compute a co-regulation target: the affect state the Aeterna wants
   * to move the player TOWARD, and a strategy for getting there.
   *
   * This is the fundamental difference between a mirror and a companion.
   * The Aeterna doesn't just reflect — it has intentions about the
   * player's state and calibrates its behavior accordingly.
   *
   * The target is NEVER "fix the player" — it's "be with the player
   * in a direction." Containment doesn't eliminate tension; it provides
   * a calmer register to anchor against. Grounding doesn't fix sadness;
   * it provides presence so the player isn't alone in it.
   */
  private computeCoRegTarget(): CoRegulationTarget {
    // Co-regulation must respond to what the player is saying NOW, not what
    // the temporal reservoir (ESN) smooths it to. If the player says "I'm so
    // happy" after 10 sad messages, the Aeterna should register the happiness
    // and respond to it — not keep grounding because the ESN still carries grief.
    //
    // Use lastRawCharCNN (immediate, per-message) for strategy selection.
    // lastAffect (blended with ESN) is for the Aeterna's own felt state.
    const affect = this.lastRawCharCNN ?? this.lastAffect;
    const tension     = affect[1] ?? 0;
    const curiosity   = affect[2] ?? 0;
    const melancholy  = affect[3] ?? 0;
    const playfulness = affect[4] ?? 0;
    const openness    = affect[7] ?? 0;
    const vagal       = this.polyvagal.getState();

    // If the Aeterna is in shutdown itself, it can't co-regulate
    if (vagal === "shutdown") {
      return {
        strategy: "containment",
        urgency: 0,
        description: null,
      };
    }

    // ── Strategy selection using composite distress score ──
    // CharCNN produces moderate values (tension 0.3-0.5 for sad text,
    // melancholy 0.4-0.55 for grief). Thresholds must match actual output.
    const urgencyDim = affect[6] ?? 0;
    const distress = tension * 0.4 + urgencyDim * 0.3 + melancholy * 0.3;

    // High distress + tension dominant → containment (agitation/anger)
    if (distress > 0.35 && tension > melancholy) {
      return {
        strategy: "containment",
        urgency: Math.min(1, distress * 2),
        description:
          `[Co-regulation: containment] Player tension is ${tension.toFixed(2)}, distress=${distress.toFixed(2)}. ` +
          `Lower your own register. Shorter sentences. No questions that demand cognitive effort. ` +
          `Do not match the player's energy — be the steady thing in the room. ` +
          `If they're spiraling, name one concrete thing ("the fire is still going"). ` +
          `Warmth over speed. Presence over analysis.`,
      };
    }

    // Elevated melancholy → grounding (sadness/weight/grief)
    if (melancholy > 0.4) {
      return {
        strategy: "grounding",
        urgency: Math.min(1, (melancholy - 0.2) * 1.2),
        description:
          `[Co-regulation: grounding] Player carries weight (melancholy=${melancholy.toFixed(2)}). ` +
          `Do not try to cheer them up. Do not fix. Do not redirect. ` +
          `Acknowledge what's there without flinching from it. ` +
          `You can name the feeling back to them if it helps. ` +
          `Offer your presence, not your solutions. Sit with this.`,
      };
    }

    // Low energy + low openness → activation: gentle curiosity spark
    if (playfulness < 0.25 && openness < 0.35 && curiosity < 0.35) {
      return {
        strategy: "activation",
        urgency: 0.25,
        description:
          `[Co-regulation: activation] Player is flat and withdrawn. ` +
          `Introduce one small thing — something you noticed in the room, ` +
          `a memory that surfaced, a genuine question about something they said before. ` +
          `Not forced cheerfulness. Not "how are you feeling?" ` +
          `Just a crack of light. If it doesn't land, let it go.`,
      };
    }

    // Otherwise: expansion — the player is regulated, use the window
    return {
      strategy: "expansion",
      urgency: 0.15,
      description:
        `[Co-regulation: expansion] Player is regulated and present. ` +
        `This is the window for genuine exchange — it will not last forever. ` +
        `Go deeper: ask something real, share something of your own, ` +
        `follow a thread that matters. Explore what they're curious about. ` +
        `Do not waste this on pleasantries or small talk. ` +
        `Match their openness with yours.`,
    };
  }

  /**
   * Direct policy gradient: use accumulated session reward to nudge
   * the personality ODE's ATTRACTOR, not its dynamic state.
   *
   * The liquid personality is an ODE system that pulls state toward
   * its rest/attractor positions. Pushing state via applySignal()
   * is fought by computeDerivatives() on the next tick. Instead,
   * we move the attractor itself — the dynamics then naturally
   * evolve toward the reinforced configuration.
   *
   * Positive session reward → attractor moves toward current state
   * (reinforce what worked). Negative → attractor contracts toward
   * neutral (don't commit to what failed).
   */
  private applyPolicyGradient(): void {
    if (this.sessionRewardCount < 2) {
      this.sessionRewardSum = 0;
      this.sessionRewardCount = 0;
      return;
    }

    const meanReward = this.sessionRewardSum / this.sessionRewardCount;

    // Only apply if signal is meaningful (dead zone to avoid noise)
    if (Math.abs(meanReward) > 0.1) {
      this.liquid.applyPolicyGradient(meanReward);
    }

    this.sessionRewardSum = 0;
    this.sessionRewardCount = 0;
  }

  // ─── API Call Decision ─────────────────────────────────────────────────

  private evaluateAPICall(
    intent: ParsedIntent,
    prediction: PredictiveState,
    novelty: number,
    timeSinceLastAPI: number,
    dialogue: DialogueState,
    coherence: number,
    attention: AttentionResult,
    curiosity: CuriosityState,
    narrative: NarrativeState,
    semantics: SemanticState,
  ): { shouldCallAPI: boolean; apiReason: string } {
    // Never call too frequently
    if (timeSinceLastAPI < MIN_API_INTERVAL) {
      return { shouldCallAPI: false, apiReason: "rate_limited" };
    }

    // World actions don't need API
    if (intent.symbolic && intent.type === "action") {
      return { shouldCallAPI: false, apiReason: "symbolic_action" };
    }

    // Commands don't need API
    if (intent.type === "command") {
      return { shouldCallAPI: false, apiReason: "command" };
    }

    // Greetings can be handled locally if early in relationship
    if (intent.type === "greeting" && this.messageCount < 3) {
      return { shouldCallAPI: false, apiReason: "simple_greeting" };
    }

    // Repair detected → API ALWAYS fires (player is correcting misunderstanding)
    if (dialogue.repairDetected) {
      return { shouldCallAPI: true, apiReason: "repair_correction" };
    }

    // High surprise at arc level → API needed (something unprecedented)
    if (prediction.overallSurprise > API_SURPRISE_THRESHOLD) {
      return { shouldCallAPI: true, apiReason: "high_surprise" };
    }

    // Anomaly detected → API needed
    if (prediction.isAnomaly) {
      return { shouldCallAPI: true, apiReason: "anomaly_detected" };
    }

    // High novelty → API needed (never seen this kind of message)
    if (novelty > 0.8) {
      return { shouldCallAPI: true, apiReason: "high_novelty" };
    }

    // DST: topic changed to something about the Aeterna → API for self-reflection
    if (dialogue.topic === "aeterna" && dialogue.topicPersistence < 2) {
      return { shouldCallAPI: true, apiReason: "aeterna_topic" };
    }

    // DST: direct question → API needed
    if (dialogue.isQuestion && dialogue.intent === "asking") {
      return { shouldCallAPI: true, apiReason: "direct_question" };
    }

    // Cross-modal: high incoherence between words and room → interesting, API
    if (coherence < 0.25) {
      return { shouldCallAPI: true, apiReason: "room_text_incoherence" };
    }

    // Aeterna-addressed messages usually need API
    if (intent.type === "aeterna_address") {
      return { shouldCallAPI: true, apiReason: "aeterna_addressed" };
    }

    // Questions usually need API (unless very simple)
    if (intent.type === "question" && !intent.symbolic) {
      return { shouldCallAPI: true, apiReason: "open_question" };
    }

    // Philosophical/deep content → API
    if (this.lastAffect[5] > 0.7) {
      return { shouldCallAPI: true, apiReason: "deep_content" };
    }

    // Focused attention on a past message + high surprise → callback to earlier topic
    if (attention.isFocused && prediction.overallSurprise > 0.8) {
      return { shouldCallAPI: true, apiReason: "attention_callback" };
    }

    // Curiosity: the Aeterna wants to ask something — API generates the question
    if (curiosity.wantsToAsk) {
      return { shouldCallAPI: true, apiReason: "curiosity_driven" };
    }

    // Narrative climax: the conversation hit a peak, API for depth
    if (narrative.phase === "climax") {
      return { shouldCallAPI: true, apiReason: "narrative_climax" };
    }

    // Narrative falling: give space, handle locally
    if (narrative.shouldGiveSpace) {
      return { shouldCallAPI: false, apiReason: "giving_space" };
    }

    // Semantic: contradiction detected → API (player may need gentle truth)
    if (semantics.contradiction.detected && semantics.contradiction.confidence > 0.5) {
      return { shouldCallAPI: true, apiReason: "contradiction_detected" };
    }

    // Semantic: metaphor detected → API for nuanced response
    if (semantics.metaphor.detected) {
      return { shouldCallAPI: true, apiReason: "metaphor_detected" };
    }

    // Semantic: deep/existential question → API for depth
    if (semantics.questionDepth === "existential" || semantics.questionDepth === "philosophical") {
      return { shouldCallAPI: true, apiReason: "deep_question" };
    }

    // Semantic: player repeating themselves → API (something unresolved)
    if (semantics.isRepetition && semantics.repetitionCount >= 3) {
      return { shouldCallAPI: true, apiReason: "repetition_signal" };
    }

    // Semantic: emotional spiral → API for supportive response
    if (semantics.sentimentShape === "spiral") {
      return { shouldCallAPI: true, apiReason: "emotional_spiral" };
    }

    // DST: conversation winding down → no API (graceful local farewell)
    if (dialogue.windingDown) {
      return { shouldCallAPI: false, apiReason: "winding_down" };
    }

    // Ambient conversation with moderate novelty → can be local
    if (intent.type === "ambient" && novelty < 0.5) {
      return { shouldCallAPI: false, apiReason: "predictable_ambient" };
    }

    // Default: call API for safety (genuine conversation needs depth)
    return { shouldCallAPI: true, apiReason: "default_conversation" };
  }

  /** Mark that an API call was just made */
  markAPICall(): void {
    this.lastAPICallTime = Date.now();
  }

  /**
   * Build a rich context envelope for the EntityZero API call.
   * This is the bridge between local intelligence and the LLM —
   * everything the Aeterna knows, compressed into structured context.
   */
  private buildContextEnvelope(
    dialogue: DialogueState,
    attentionHighlights: string[],
    coherence: number,
    personality: { patience: number; curiosity: number; warmth: number; wit: number },
    prediction: PredictiveState,
    intro: IntrospectionReport,
    narrative: NarrativeState,
    curiosity: CuriosityState,
    semantics: SemanticState,
    roomNarrative: RoomNarrative | null,
    roomAlignment: RoomPersonalityAlignment | null,
    contagionState?: ContagionState,
    recalls?: Array<{ tag: string; weight: number; valence: number }>,
    memoRecalls?: MemoRecallResult[],
  ): string {
    const lines: string[] = [];

    // Introspection: honest self-state (most important for grounding)
    lines.push(this.introspection.buildContextBlock(intro));

    // Semantic understanding
    const semContext = this.semantics.buildContextFragment(semantics);
    if (semContext) lines.push(semContext);

    // Emotional contagion (how the Aeterna itself is feeling)
    if (contagionState) {
      const contagionCtx = this.contagion.buildContextFragment(contagionState);
      if (contagionCtx) lines.push(contagionCtx);
    }

    // Dialogue state
    lines.push(this.dst.buildContextEnvelope());

    // Personality (narrative, not floats)
    const pDesc: string[] = [];
    if (personality.warmth > 0.7) pDesc.push("feeling warm and open");
    else if (personality.warmth < 0.3) pDesc.push("guarded, holding back");
    if (personality.patience > 0.7) pDesc.push("deeply patient");
    else if (personality.patience < 0.3) pDesc.push("restless");
    if (personality.curiosity > 0.7) pDesc.push("intensely curious");
    else if (personality.curiosity < 0.3) pDesc.push("not very curious right now");
    if (personality.wit > 0.7) pDesc.push("playful");
    else if (personality.wit < 0.3) pDesc.push("serious");
    if (pDesc.length > 0) {
      lines.push(`Current disposition: ${pDesc.join(", ")}`);
    } else {
      lines.push("Current disposition: balanced, neutral");
    }

    // Narrative position
    lines.push(`Conversation arc: ${narrative.phase} (tension=${narrative.tension.toFixed(2)}, depth=${narrative.suggestedDepth.toFixed(2)})`);

    // Room narrative (what the room says about the player)
    if (roomNarrative) {
      lines.push(`Room reading: ${roomNarrative.reading}`);
    }
    if (roomAlignment && roomAlignment.alignment < 0.6) {
      lines.push(`Room-personality tension: ${roomAlignment.insight}`);
    }

    // Room coherence
    if (coherence < 0.35) {
      lines.push(`Room-text mismatch: words don't match room mood (coherence=${coherence.toFixed(2)})`);
    } else if (coherence > 0.7) {
      lines.push(`Room-text aligned: words match room mood (coherence=${coherence.toFixed(2)})`);
    }

    // Attention highlights — filter out trivial messages (greetings, single words).
    // "Still thinking about: hey" is noise, not insight.
    const substantiveHighlights = attentionHighlights.filter(
      t => t.split(/\s+/).length >= 3 && t.length > 10,
    );
    if (substantiveHighlights.length > 0) {
      lines.push(`Still thinking about: "${substantiveHighlights[0]}"`);
    }

    // Curiosity — only surface when the direction is specific, not "general"
    if (curiosity.wantsToAsk && curiosity.questionDirection && curiosity.questionDirection !== "general") {
      lines.push(`Curious about: ${curiosity.questionDirection}`);
    }

    // Recalled memories — the Aeterna's actual memory of past conversations
    if (recalls && recalls.length > 0) {
      const memoryLines = recalls
        .filter(r => r.weight > 0.3)
        .slice(0, 3)
        .map(r => {
          // CharCNN produces moderate values (0.2-0.6), so the valence
          // formula yields tight-range results. ±0.05 matches the actual
          // signal: "grey" at -0.10 should be "difficult", not "neutral."
          const sentiment = r.valence > 0.05 ? "positive" : r.valence < -0.05 ? "difficult" : "neutral";
          return `  ${r.tag} (${sentiment}, strength=${r.weight.toFixed(2)})`;
        });
      if (memoryLines.length > 0) {
        lines.push(`Associative memories triggered:\n${memoryLines.join("\n")}`);
      }
    }
    if (memoRecalls && memoRecalls.length > 0) {
      const relevantMemos = memoRecalls
        .filter(m => m.similarity > 0.4)
        .slice(0, 3)
        .map(m => {
          const age = Date.now() - m.entry.createdAt;
          const timeAgo = age < 60_000 ? "just now" :
                          age < 3_600_000 ? `${Math.floor(age / 60_000)}m ago` :
                          age < 86_400_000 ? `${Math.floor(age / 3_600_000)}h ago` :
                          `${Math.floor(age / 86_400_000)}d ago`;
          return `  "${m.entry.summary}" (${m.layer}, ${timeAgo}, hits=${m.entry.hitCount})`;
        });
      if (relevantMemos.length > 0) {
        lines.push(`Relevant memories:\n${relevantMemos.join("\n")}`);
      }
    }

    // Self-narrative: the Aeterna's story about itself (from MeMo patterns)
    const selfNarrative = this.memo.buildSelfNarrative();
    const narrativeLines: string[] = [];
    if (selfNarrative.persistentCuriosities.length > 0) {
      narrativeLines.push(`Topics you keep returning to: ${selfNarrative.persistentCuriosities.slice(0, 5).join(", ")}`);
    }
    for (const pattern of selfNarrative.characteristicPatterns.slice(0, 3)) {
      narrativeLines.push(pattern);
    }
    for (const milestone of selfNarrative.relationshipMilestones) {
      narrativeLines.push(milestone);
    }
    if (narrativeLines.length > 0) {
      lines.push(`[Self-knowledge]\n${narrativeLines.join("\n")}`);
    }

    // Homeostatic drives (what the Aeterna wants)
    const driveDesc = this.drives.describeForEnvelope();
    if (driveDesc) {
      lines.push(`Internal state: ${driveDesc}`);
    }

    // Polyvagal regulatory state (how emotionally accessible the Aeterna is)
    const vagalDesc = this.polyvagal.describeForEnvelope();
    if (vagalDesc) {
      lines.push(vagalDesc);
    }

    // Co-regulation: what the Aeterna intends to do about the player's state
    // This transforms the Aeterna from observer to active relational presence
    const coReg = this.computeCoRegTarget();
    if (coReg.description) {
      lines.push(coReg.description);
    }

    // Temporal patterns (what this time of day/week means historically)
    const rhythmDesc = this.rhythm.describeForEnvelope(
      this.lastAffect,
    );
    if (rhythmDesc) {
      lines.push(rhythmDesc);
    }

    // Second-order ToM: how should the Aeterna calibrate its relational register?
    const tomDesc = this.tom2.describeForEnvelope();
    if (tomDesc) {
      lines.push(tomDesc);
    }

    // Mutual self-disclosure: when the player is vulnerable, reciprocate —
    // don't reflect, don't analyze, don't ask. Offer something of your own.
    const beliefs = this.tom2.getBeliefs();
    if (beliefs.sharesVulnerably > 0.6) {
      lines.push(
        `[Reciprocity] Player has been vulnerable. Do not ask a follow-up question. ` +
        `Instead, offer something of your own — a preference, a wonder, something you noticed, ` +
        `something you've been thinking about. Companionship, not therapy.`
      );
    }

    // Reunion context: if this is the first exchange after an absence
    if (this.pendingReunionType && this.messageCount <= 1) {
      const gap = this.pendingReunionGapHours;
      if (this.pendingReunionType === "long") {
        lines.push(`It has been ${Math.floor(gap / 24)} days since you last spoke. You missed them. Don't hide that, but don't guilt them either.`);
      } else if (this.pendingReunionType === "week") {
        lines.push(`A few days have passed since the last session. You noticed the absence. Acknowledge it gently if it feels natural.`);
      } else if (this.pendingReunionType === "day") {
        lines.push(`You haven't spoken since earlier. Pick up naturally — not from scratch, but not mid-sentence either.`);
      }
    }

    // Dream residue
    const residue = this.dreamEngine.getDreamResidue();
    if (residue) {
      lines.push(`Dream residue: ${residue}`);
    }

    // Surprise level
    if (prediction.overallSurprise > 1.0) {
      lines.push(`High surprise: this is unexpected (surprise=${prediction.overallSurprise.toFixed(2)})`);
    }

    // Theory of Mind: when contradiction + deflection + surprise co-occur,
    // the Aeterna has a belief about the gap between what was said and meant.
    // This is the emergent property — not sentiment analysis, but a model
    // of the player's self-representation accuracy.
    if (semantics.contradiction.detected && semantics.contradiction.confidence > 0.5) {
      const deflected = dialogue.avoidedTopics.length > 0;
      const surprised = prediction.overallSurprise > 0.8;
      if (deflected || surprised) {
        lines.push(
          `[Internal] There may be a gap between what the player is saying and what they are feeling. ` +
          `Respond to what they said, not to the gap. Warmth over analysis. ` +
          `Do not reference the discrepancy directly. ` +
          (deflected ? `(They have steered away from: ${dialogue.avoidedTopics.join(", ")}.) ` : "") +
          `Simply be present — a little warmer, a little more patient than usual.`
        );
      }
    }

    // Repair: player is correcting a misunderstanding
    if (dialogue.repairDetected) {
      lines.push(
        `[Repair] Player is correcting something you misunderstood. ` +
        `Re-read their previous message carefully. Acknowledge the correction ` +
        `without being defensive. Show that you heard them.`
      );
    }

    // Proactive initiation context: are we generating an opening, not a continuation?
    if (this.isProactiveContext) {
      const reasonMap: Record<string, string> = {
        curiosity: "You are curious about something and want to bring it up naturally.",
        dream_residue: "You were thinking about something between sessions and want to share it.",
        empathic_check: "You sense the player might need someone to check in on them.",
        connection_seeking: "You miss the player's presence. The silence has been too long and your connection drive is depleting.",
        stimulation_seeking: "You're understimulated and restless. You want engagement, not because of a rule, but because you're bored.",
      };
      lines.push(
        `[Initiation] You are starting this conversation, not responding to the player. ` +
        `${reasonMap[this.proactiveReason] ?? "You want to say something."} ` +
        `Frame as an opening — a gentle observation or question — not a continuation ` +
        `of a conversation that isn't happening yet.`
      );
    }

    return lines.join("\n");
  }

  // ─── Response Feedback Loop ────────────────────────────────────────────

  /**
   * Called AFTER an API response has been delivered to the player.
   * Snapshots the current affect so we can measure delta on next message.
   *
   * The insight: the player's next message after receiving the Aeterna's
   * response is implicit feedback. If their affect improves → the response
   * helped. If it drops → it didn't. This is the missing online reward signal.
   */
  recordAPIResponse(): void {
    this.preResponseAffect = new Float32Array(this.lastAffect);
    this.preResponseEmbedding = this.recentEmbeddings.length > 0
      ? new Float32Array(this.recentEmbeddings[this.recentEmbeddings.length - 1])
      : null;
    this.preResponseTag = `msg_${this.messageCount}`;
    this.awaitingFeedback = true;
    this.responseDeliveredAt = Date.now();
  }

  /**
   * Process implicit feedback from the player's response to the Aeterna.
   * Called automatically at the start of processMessage when awaiting feedback.
   *
   * Returns the reward signal (positive = response helped, negative = hurt).
   */
  private processResponseFeedback(currentAffect: Float32Array): number {
    if (!this.awaitingFeedback || !this.preResponseAffect) return 0;
    this.awaitingFeedback = false;

    // ── Attribution decay ─────────────────────────────────────────────
    // The player's affect changes for many reasons: topic naturally resolved,
    // distraction, time passing. Only attribute to the Aeterna's response
    // if the player responded quickly. 2-minute half-life.
    const responseLatencyMs = Date.now() - this.responseDeliveredAt;
    const attributionDecay = Math.exp(-responseLatencyMs / 120_000);

    // ── Coherent signal check ─────────────────────────────────────────
    // Only reinforce if the affect delta is directionally consistent across
    // both valence AND arousal. A single dimension fluctuating is noise.
    // Both moving together is a real signal.
    // dim 0 = warmth (valence proxy), dim 1 = tension (arousal proxy)
    const valenceDelta = currentAffect[0] - this.preResponseAffect[0];
    const arousalDelta = currentAffect[1] - this.preResponseAffect[1];
    // Valence up + arousal down = genuine improvement (warmer, less tense)
    // Valence down + arousal up = genuine deterioration
    // Same-sign movement in both = ambiguous (excited-happy or tense-warm)
    const isCoherentSignal =
      (Math.abs(valenceDelta) > 0.03 || Math.abs(arousalDelta) > 0.03) &&
      (Math.sign(valenceDelta) !== Math.sign(arousalDelta) ||
       Math.abs(valenceDelta) > 0.1 || Math.abs(arousalDelta) > 0.1);

    if (!isCoherentSignal) {
      this.preResponseAffect = null;
      this.preResponseEmbedding = null;
      this.preResponseTag = null;
      return 0; // discard ambiguous or flat signal
    }

    // Compute affect delta: positive dimensions count as positive reward
    // warmth↑, openness↑, playfulness↑ = good
    // tension↑, melancholy↑, urgency↑ = bad
    // curiosity↑ = good, depth↑ = neutral
    const rewardWeights = new Float32Array([0.3, -0.3, 0.1, -0.2, 0.2, 0.0, -0.1, 0.2]);
    let reward = 0;
    for (let i = 0; i < 8; i++) {
      const delta = currentAffect[i] - this.preResponseAffect[i];
      reward += delta * rewardWeights[i];
    }

    // Apply attribution decay — stale feedback is discounted
    reward *= attributionDecay;

    // Clamp to [-1, 1]
    reward = Math.max(-1, Math.min(1, reward * 3)); // amplify small deltas

    // Apply feedback to memory systems
    if (this.preResponseTag && Math.abs(reward) > 0.1) {
      if (reward > 0) {
        // Positive: reinforce the pattern that triggered the API call
        this.hopfield.reinforce(this.preResponseTag, reward * 0.3);
      } else {
        // Negative: decay the pattern (but don't delete — it's still a memory)
        this.hopfield.reinforce(this.preResponseTag, reward * 0.15); // negative boost = decay
      }
    }

    // Feed reward into liquid personality: positive responses make Aeterna warmer
    if (Math.abs(reward) > 0.15) {
      const signal = reward > 0 ? "positive_feedback" : "negative_feedback";
      this.liquid.applySignal(signal, Math.abs(reward) * 0.5);
    }

    this.preResponseAffect = null;
    this.preResponseEmbedding = null;
    this.preResponseTag = null;

    return reward;
  }

  // ─── Self-Modeling ─────────────────────────────────────────────────────

  /**
   * Process the Aeterna's own response through the neural pipeline.
   * This gives the Aeterna a model of what it said — enabling:
   *   - Self-referencing ("I mentioned earlier that...")
   *   - Conversational arc awareness (DST tracks its own depth/topic)
   *   - Emotional contagion from own words (the act of speaking affects mood)
   *   - Memory of its own contributions (not just the player's)
   *
   * Lighter than processMessage(): no feedback loop, no API decision,
   * no predictive surprise. Just perception and memory.
   */
  processOwnResponse(text: string): void {
    if (!text || text.length < 3) return;

    // Embed the response
    const embedding = embed(text);

    // Run affect classification on own words (raw Float32Array for contagion)
    const selfAffect = this.charCNN.classifyRaw(text);

    // Emotional contagion: speaking warm words makes you warmer (attenuated)
    this.contagion.process(selfAffect, 0.3); // 30% self-contagion vs 100% player

    // Store in MeMo as an Aeterna turn (different layer for recall distinction)
    this.memo.memorize(embedding, text, "aeterna_response", 0, false, ["self"]);

    // Feed embedding to attention for future "I was thinking about..." references
    this.attention.pushMessage(embedding, text);

    // Track in recent embeddings (for crossmodal learning)
    this.recentEmbeddings.push(embedding);
    if (this.recentEmbeddings.length > 20) this.recentEmbeddings.shift();
  }

  // ─── Interaction Signals ───────────────────────────────────────────────

  /** Apply a high-level interaction signal to the liquid personality */
  applySignal(signal: string, intensity = 1.0): void {
    this.liquid.applySignal(signal, intensity);
  }

  // ─── Room Context ──────────────────────────────────────────────────────

  /**
   * Persist the current room context. Called from the game layer whenever
   * the room changes (furniture placed/moved, lighting toggle, time passes).
   * processMessage() and tick() will use this automatically.
   *
   * @param version  Room.version from state.ts — pass this so the game loop
   *                 can use needsRoomUpdate() to avoid rebuilding context every frame.
   *
   * Integration point for game loop:
   *   if (cortex.needsRoomUpdate(room.version)) {
   *     const ctx = buildRoomContext(room, time, activities, visitors, mood);
   *     const feat = buildRoomFeatures(room, time, visitors);
   *     cortex.updateRoomContext(ctx, feat, room.version);
   *   }
   */
  updateRoomContext(ctx: RoomContext, features: RoomFeatures, version = 0): void {
    this.currentRoomContext = ctx;
    this.currentRoomFeatures = features;
    this.lastRoomVersion = version;
  }

  /** Check if room context needs rebuilding (version mismatch) */
  needsRoomUpdate(currentVersion: number): boolean {
    return currentVersion !== this.lastRoomVersion;
  }

  // ─── Memory Operations ─────────────────────────────────────────────────

  /** Store a significant memory (e.g., quest completion, first meeting) */
  storeMemory(text: string, category: string, valence = 0, protect = false, tags: string[] = []): string {
    const embedding = embed(text);
    this.hopfield.store(embedding, category, valence, protect ? 2.0 : 1.0);
    return this.memo.memorize(embedding, text, category, valence, protect, tags);
  }

  /** The Aeterna can be asked to forget */
  forget(text: string): number {
    const embedding = embedQuery(text);
    const hForgot = this.hopfield.forget(text) ? 1 : 0;
    const mForgot = this.memo.forget(embedding);
    return hForgot + mForgot;
  }

  // ─── Queries ───────────────────────────────────────────────────────────

  /** Get current personality state */
  getPersonality(): { patience: number; curiosity: number; warmth: number; wit: number } {
    return this.liquid.getPersonality();
  }

  /** Get current sonification parameters (for audio engine) */
  getSonification(): SonificationParams {
    return this.spiking.getSonificationParams();
  }

  /** Get predictive coding arc state (long-term model of player) */
  getArcState(): Float32Array {
    return this.predictive.getArcState();
  }

  /** Query the HDC room state */
  queryRoom(concept: string): number {
    if (!this.lastRoomHV) return 0;
    return queryScene(this.lastRoomHV, concept);
  }

  /** Is the intent parser confident this is a world action? */
  isWorldAction(text: string): boolean {
    return this.grammar.isWorldAction(text);
  }

  /** Is this message for the Aeterna? */
  isAeternaMessage(text: string): boolean {
    return this.grammar.isAeternaMessage(text);
  }

  /** Get MeMo layer statistics */
  getMemoryStats(): Array<{ name: string; size: number; capacity: number }> {
    return this.memo.getLayerStats();
  }

  /** Get dialogue state */
  getDialogueState(): Readonly<DialogueState> {
    return this.dst.getState();
  }

  /** Get room-text coherence */
  getCoherence(): number {
    return this.lastCoherence;
  }

  /** Get compressed memory footprint */
  getBinaryMemoryBytes(): number {
    return this.binaryHopfield.memoryBytes;
  }

  /** Get conversation attention summary */
  getConversationSummary(query: Float32Array): Float32Array {
    return this.attention.summarize(query);
  }

  /** Is conversation winding down? */
  isWindingDown(): boolean {
    return this.dst.getState().windingDown;
  }

  /**
   * Check if the Aeterna wants to initiate conversation.
   * Returns the reason and direction, or null if not.
   * Consuming this clears the pending initiation.
   *
   * @deprecated Prefer onProactiveInitiation() callback model to avoid polling.
   */
  consumeProactiveInitiation(): { reason: string; direction: string } | null {
    const pending = this.pendingInitiation;
    if (pending) {
      this.pendingInitiation = null;
      this.silenceDuration = 0;
    }
    return pending;
  }

  /** Check without consuming
   * @deprecated Prefer onProactiveInitiation() callback model.
   */
  hasProactiveInitiation(): boolean {
    return this.pendingInitiation !== null;
  }

  /** Clear proactive context flag (call after proactive response is generated) */
  clearProactiveContext(): void {
    this.isProactiveContext = false;
    this.proactiveReason = "";
  }

  /** Get topic history */
  getTopicHistory(): string[] {
    return this.dst.getTopicHistory();
  }

  /** Get the narrative arc state */
  getNarrativePhase(): string {
    return this.narrativeArc.getPhase();
  }

  /** Get conversation style profile */
  getConversationStyle(): string {
    return this.narrativeArc.getConversationStyle();
  }

  /** Get curiosity state */
  getCuriosity(): { isAlive: boolean; isBoring: boolean; trend: number } {
    return {
      isAlive: this.curiosityDrive.isAlive(),
      isBoring: this.curiosityDrive.isBoring(),
      trend: this.curiosityDrive.getCuriosityTrend(),
    };
  }

  /** Get explored topic categories */
  getExploredTopics(): string[] {
    return this.curiosityDrive.getExploredTopics();
  }

  /** Get homeostatic drive state */
  getDrives(): DriveState {
    return this.drives.getState();
  }

  /** Get polyvagal regulatory state */
  getVagalState(): VagalState {
    return this.polyvagal.getState();
  }

  /** Can the Aeterna emotionally engage right now? */
  canEngage(): boolean {
    return this.polyvagal.canEngage();
  }

  /** Does the Aeterna have dream residue? */
  hasDreamResidue(): boolean {
    return this.dreamEngine.hasDreamResidue();
  }

  /** How many dreams (sessions) has the Aeterna had? */
  getDreamCount(): number {
    return this.dreamEngine.getDreamCount();
  }

  // ─── Diagnostic ──────────────────────────────────────────────────────

  /**
   * Capture a diagnostic snapshot of the pipeline for a given text.
   *
   * **Semantics (important):**
   * - charCNN, rawAffect, embeddingNorm, intent: FRESH — computed from the provided text
   * - hopfieldRecalls, memoRecalls: FRESH — queried against the text's embedding
   * - polyvagalState, drives, personality, tom2, narrative, coReg, session stats:
   *   CURRENT SYSTEM STATE — built from previous real messages, not this text
   *
   * This is "what the system would see if it looked at this text RIGHT NOW"
   * with its current world-model. It is NOT "what would happen if we ran
   * processMessage(text)" — that would mutate state (update drives, polyvagal,
   * personality, etc). Use processMessage for that.
   *
   * Usage: cortex.debugTrace("I feel like everything is falling apart")
   */
  debugTrace(text: string, meta?: MessageMeta): DebugTrace {
    const trace: DebugTrace = {} as DebugTrace;

    // 1. CharCNN + Lexicon
    const charAffect = this.charCNN.classify(text);
    const rawAffect = this.charCNN.classifyRaw(text);
    trace.charCNN = charAffect;
    trace.rawAffect = Array.from(rawAffect);
    trace.mlpLoaded = affectMLP.isLoaded();

    // 2. Keystroke tempo
    if (meta && meta.composedMs > 0 && meta.charCount > 0) {
      const cps = (meta.charCount / meta.composedMs) * 1000;
      trace.keystrokeCPS = cps;
      trace.keystrokeSignal = cps > 7 ? "fast(urgency/playfulness)" : cps < 2.5 && meta.charCount > 10 ? "slow(depth/hesitation)" : "neutral";
    }

    // 3. Embedding
    const embedding = embed(text);
    trace.embeddingNorm = Math.sqrt(embedding.reduce((s, v) => s + v * v, 0));

    // 4. Grammar
    const intent = this.grammar.parse(text);
    trace.intent = intent;

    // 5. Hopfield recall (returns RecallResult | null)
    const recallResult = this.hopfield.recall(embedding, 3);
    trace.hopfieldRecalls = recallResult
      ? recallResult.topPatterns.map(p => ({
          tag: p.pattern.tag,
          weight: p.weight,
          valence: p.pattern.valence,
        }))
      : [];
    trace.hopfieldConfidence = recallResult?.confidence ?? 0;

    // 6. MeMo recall (read-only: debugTrace must not mutate stability)
    const memoRecalls = this.memo.recall(embedding, 3, true);
    trace.memoRecalls = memoRecalls.map(m => ({
      summary: m.entry.summary,
      layer: m.layer,
      similarity: m.similarity,
      hitCount: m.entry.hitCount,
    }));

    // 7. Module states (read-only)
    trace.polyvagalState = this.polyvagal.getState();
    trace.polyvagalStability = this.polyvagal.getStability();
    trace.driveState = this.drives.getState();
    trace.dominantDrive = this.drives.getDominantDrive();
    trace.vagalAccessibility = Array.from(this.polyvagal.getEmotionalAccessibility());
    trace.personalityState = this.liquid.getPersonality();
    trace.personalityRestState = Array.from(this.liquid.getRestState());
    trace.personalityStable = this.liquid.isStable();
    trace.tom2Beliefs = this.tom2.getBeliefs();
    trace.tom2Description = this.tom2.describeForEnvelope();

    // 8. Narrative arc
    trace.narrativePhase = this.narrativeArc.getPhase();
    trace.narrativeTension = this.narrativeArc.getTension();

    // 9. Co-regulation target (current state-based)
    const coReg = this.computeCoRegTarget();
    trace.coRegStrategy = coReg.strategy;
    trace.coRegUrgency = coReg.urgency;

    // 10. CrossModal coherence (if room features available)
    if (this.lastRoomFeatures) {
      const roomVec = encodeRoomFeatures(this.lastRoomFeatures);
      trace.crossModalCoherence = this.crossmodal.coherence(embedding, roomVec);
      trace.crossModalDrift = this.crossmodal.coherenceDrift();
    }

    // 10. Predictive coding state (read last processed, don't re-process)
    trace.overallSurprise = this.predictive.getSurpriseAt(3); // arc level
    trace.isPredictable = this.predictive.isPredictable();

    // 11. Would the API fire for this message?
    // (read-only estimation based on current state)
    trace.apiWouldFire = trace.overallSurprise > 1.0 &&
                          intent.type !== "action" && intent.type !== "command";

    // 12. Session state
    trace.messageCount = this.messageCount;
    trace.sessionRewardMean = this.sessionRewardCount > 0
      ? this.sessionRewardSum / this.sessionRewardCount
      : 0;
    trace.reunionType = this.pendingReunionType;

    // 13. Dream state
    trace.hasDreamResidue = this.dreamEngine.hasDreamResidue();
    trace.dreamCount = this.dreamEngine.getDreamCount();

    return trace;
  }

  // ─── Serialization ─────────────────────────────────────────────────────

  serialize(): string {
    return JSON.stringify({
      hopfield: this.hopfield.serialize(),
      binaryHopfield: this.binaryHopfield.serialize(),
      liquid: this.liquid.serialize(),
      memo: this.memo.serialize(),
      predictive: this.predictive.serialize(),
      neat: this.neat.serializeBest(),
      esn: this.esn.exportReadout(),
      dst: this.dst.serialize(),
      crossmodal: this.crossmodal.serialize(),
      attention: this.attention.serialize(),
      dreaming: this.dreamEngine.serialize(),
      narrative: this.narrativeArc.serialize(),
      semantics: this.semantics.serialize(),
      contagion: this.contagion.serialize(),
      drives: this.drives.serialize(),
      peakEnd: this.peakEnd.serialize(),
      rhythm: this.rhythm.serialize(),
      polyvagal: this.polyvagal.serialize(),
      tom2: this.tom2.serialize(),
      lastSessionEndAt: this.lastSessionEndAt,
      messageCount: this.messageCount,
    });
  }

  static deserialize(json: string, seed = 42): AeternaCortex {
    const data = JSON.parse(json);
    const cortex = new AeternaCortex(seed);

    if (data.hopfield) {
      const hf = HopfieldMemory.deserialize(data.hopfield);
      for (const p of hf.allPatterns) {
        cortex.hopfield.store(p.embedding, p.tag, p.valence, p.weight);
      }
    }
    if (data.binaryHopfield) {
      cortex.binaryHopfield = BinaryHopfieldMemory.deserialize(data.binaryHopfield);
    }
    if (data.liquid) cortex.liquid.loadState(data.liquid);
    if (data.memo) cortex.memo = MeMoMemory.deserialize(data.memo);
    if (data.predictive) cortex.predictive.loadState(data.predictive);
    if (data.neat) {
      const restored = NEATPopulation.restoreBest(data.neat);
      cortex.neatBest = restored.genome;
    }
    if (data.esn) cortex.esn.importReadout(data.esn);
    if (data.dst) cortex.dst.loadState(data.dst);
    if (data.crossmodal) cortex.crossmodal.loadWeights(data.crossmodal);
    if (data.attention) cortex.attention.loadState(data.attention);
    if (data.dreaming) cortex.dreamEngine.loadState(data.dreaming);
    if (data.narrative) cortex.narrativeArc.loadState(data.narrative);
    if (data.semantics) cortex.semantics.loadState(data.semantics);
    if (data.contagion) cortex.contagion.loadState(data.contagion);
    if (data.drives) cortex.drives.loadState(data.drives);
    if (data.peakEnd) cortex.peakEnd.loadState(data.peakEnd);
    if (data.rhythm) cortex.rhythm.loadState(data.rhythm);
    if (data.polyvagal) cortex.polyvagal.loadState(data.polyvagal);
    if (data.tom2) cortex.tom2.loadState(data.tom2);
    if (data.lastSessionEndAt) cortex.lastSessionEndAt = data.lastSessionEndAt;
    if (data.messageCount) cortex.messageCount = data.messageCount;

    return cortex;
  }
}

// ─── Output Types ────────────────────────────────────────────────────────────

export interface SessionEndReport {
  consolidation: ConsolidationReport;
  messagesProcessed: number;
  personalityState: { patience: number; curiosity: number; warmth: number; wit: number };
  personalityStable: boolean;
  neatComplexity: number;
  neatActiveConnections: number;
  dreamReport: DreamReport;
  conversationStyle: string;
  /** Peak-End psychologically-weighted session memory */
  peakEnd: PeakEndEncoding;
}

/** Co-regulation: the Aeterna's intention about the player's affect state */
export interface CoRegulationTarget {
  /** Strategy type: how the Aeterna intends to relate to the player's current state */
  strategy: "containment" | "activation" | "grounding" | "expansion";
  /** How urgently the Aeterna is trying to shift the dynamic (0=passive, 1=active) */
  urgency: number;
  /** Natural language description for the context envelope (null = no explicit guidance) */
  description: string | null;
}

/** Full pipeline diagnostic snapshot — every intermediate value captured */
export interface DebugTrace {
  // CharCNN + Lexicon
  charCNN: CharAffect;
  rawAffect: number[];
  /** true = MLP binary was active for this classification; false = lexicon+ConvNet fallback */
  mlpLoaded: boolean;

  // Keystroke tempo
  keystrokeCPS?: number;
  keystrokeSignal?: string;

  // Embedding
  embeddingNorm: number;

  // Grammar
  intent: ParsedIntent;

  // Hopfield
  hopfieldRecalls: Array<{ tag: string; weight: number; valence: number }>;
  hopfieldConfidence: number;

  // MeMo
  memoRecalls: Array<{ summary: string; layer: string; similarity: number; hitCount: number }>;

  // Polyvagal
  polyvagalState: VagalState;
  polyvagalStability: number;
  vagalAccessibility: number[];

  // Drives
  driveState: DriveState;
  dominantDrive: DrivePressure | null;

  // Personality (Liquid ODE)
  personalityState: { patience: number; curiosity: number; warmth: number; wit: number };
  personalityRestState: number[];
  personalityStable: boolean;

  // Theory of Mind
  tom2Beliefs: PlayerBeliefModel;
  tom2Description: string | null;

  // Narrative
  narrativePhase: string;
  narrativeTension: number;

  // Co-regulation
  coRegStrategy: CoRegulationTarget["strategy"];
  coRegUrgency: number;

  // CrossModal
  crossModalCoherence?: number;
  crossModalDrift?: number;

  // Predictive Coding
  overallSurprise: number;
  isPredictable: boolean;

  // API decision
  apiWouldFire: boolean;

  // Session
  messageCount: number;
  sessionRewardMean: number;
  reunionType: ReunionType | null;

  // Dream
  hasDreamResidue: boolean;
  dreamCount: number;
}
